import React, {useState, useContext, useEffect, useRef} from "react";
import { AdContext, StrapiDataProvider, StrapiDataContext } from "./context";
import { buildInAppAdsFromSidebarAds, adMatchesKeywords, adMatchesShowTo, isLightBackground } from "./sefaria/sidebarAds";
import { adMatchesPageTypes } from "./sefaria/pageTypes";
import classNames from "classnames";
import Sefaria from "./sefaria/sefaria";
import Util from "./sefaria/util";
import {EnglishText, HebrewText, InterfaceText, OnInView, replaceNewLinesWithLinebreaks} from "./Misc";
import $ from "./sefaria/sefariaJquery";
import { NewsletterSignUpForm } from "./NewsletterSignUpForm";

// pageTypeOverride: a PAGE_TYPE value supplied by a page that knows its own type better than
// panel state can. Two call sites pass it today, both in TopicPage.jsx: the topic sidebar passes
// author_page/topic_page (decided by fetched topic data that lives in TopicPage's state — and
// TopicPage mounts this component only after that data has loaded, so the classification can
// never refine underneath a rendered ad; no flash of an ad that stops matching), and
// PortalNavSideBar passes portal_page through NavSidebar's Promo module props. Everywhere else
// the prop is absent and context.pageTypes (classified synchronously from panel state in
// ReaderApp.getUserContext) is the whole answer.
const Promotions = ({ pageTypeOverride }) => {
  // No cross-mount cache here on purpose: the processing effect below re-runs on every mount
  // (StrapiDataContext lives at the app root and still holds the payload), so a remounted
  // instance rebuilds its ads immediately. A module-global stash (Sefaria._inAppAds) used to
  // pre-fill this state — a leftover whose only real job had been masking the effect-deps bug
  // this component's other comment describes, by making ads appear after a remount.
  const [inAppAds, setInAppAds] = useState(null);
  const [matchingAds, setMatchingAds] = useState(null); // match the ads to what comes from Strapi
  const context = useContext(AdContext);
  const strapi = useContext(StrapiDataContext);
  // context.pageTypes may be absent: AdContext defaults to {} before the provider mounts.
  const activePageTypes = pageTypeOverride
    ? [...(context.pageTypes || []), pageTypeOverride]
    : context.pageTypes || [];
  useEffect(() => {
    if (strapi.dataFromStrapiHasBeenReceived) {
      // Guard against unexpected Strapi payload shapes so a processing error here so the code can never unmount the whole React tree
      try {
        const sidebarAds = strapi.strapiData?.sidebarAds;

        // Only an array is iterable here. A stale/incompatible payload may nest the ads under a wrapper object (Strapi v4's { data: [...] })
        // The wrong data type  must be treated as "no ads" rather than crashing on .forEach iterator
        if (Array.isArray(sidebarAds)) {
          setInAppAds(buildInAppAdsFromSidebarAds(sidebarAds));
        }
      } catch (error) {
        console.error("Failed to process sidebar ads from Strapi: ", error);
      }
    }
    // strapiData must be a dependency, not just the received-flag: context.js sets the flag and
    // the data in two separate setState calls, and React 16 does not batch updates made inside a
    // promise callback. The flag can therefore flip in its own commit while strapiData is still
    // null, in which case this effect runs, finds no array, and — keyed on the flag alone — would
    // never run again once the data arrived. The ad then only appeared after some later navigation
    // remounted this component.
  }, [strapi.dataFromStrapiHasBeenReceived, strapi.strapiData]);
  // dataFromStrapiHasBeenReceived will originally be null until that part is scheduled and executed
  
  useEffect(() => {
    if (inAppAds) {
      setMatchingAds(getCurrentMatchingAds());
    }
    // pageTypeOverride is a real matching input, so it belongs in the deps for the same reason
    // strapiData does in the effect above: depend on the actual inputs, not proxies for them.
  }, [context, inAppAds, pageTypeOverride]);


  function showGivenDebugMode(ad) {
    if (!ad.debug) {
      return true;
    } else if (context.isDebug == true) {
      return true;
    } else {
      return false;
    }
  }

  function getCurrentMatchingAds() {
    // TODO: refine matching algorithm to order by matchingness?
    return inAppAds.filter((ad) => {
      return (
        // Audience: truth table on adMatchesShowTo in sefaria/sidebarAds.js.
        adMatchesShowTo(ad.trigger.showTo, context.isLoggedIn) &&
        showGivenDebugMode(ad) &&
        ad.trigger.interfaceLang === context.interfaceLang &&
        context.dt >= ad.trigger.startTimeDate &&
        context.dt <= ad.trigger.endTimeDate &&
        // Both targeting axes must pass. Page type: an all_pages ad passes everywhere, a typed
        // ad needs the current page's kind among activePageTypes. Keywords: see the semantics
        // table on adMatchesKeywords in sefaria/sidebarAds.js — an empty keyword rule passes
        // everywhere, includes require a matching page keyword, and exclusions only subtract
        // from keyword-bearing pages.
        adMatchesPageTypes(ad.trigger.pageType, activePageTypes) &&
        adMatchesKeywords(ad.trigger, context.keywordTargets || [])
      );
    });
  }

  return matchingAds
    ? matchingAds.map((ad) => (
        <SidebarAd context={context} matchingAd={ad} key={ad.campaignId} />
      ))
    : null;
};

function trackSidebarAdImpression(ad) {
  console.log(ad.campaignId + " has been seen");
  gtag("event", "promo_viewed", {
    campaignID: ad.campaignId,
    adType: "sidebar",
  });
}

function trackSidebarAdClick(ad) {
  gtag("event", "promo_clicked", {
    campaignID: ad.campaignId,
    adType: "sidebar",
  });
}
const cookie = Sefaria._inBrowser ? $.cookie : Sefaria.util.cookie;
const GDocAdvertText = () => {
    const learnMoreLink = "https://voices.sefaria.org/sheets/529099?origin=AddToSheetsPromo"
    return    <InterfaceText>
                <EnglishText> Add texts directly to your Google Docs with our <span id="newExtension">new extension</span>! <a href={learnMoreLink} data-target-module={Sefaria.VOICES_MODULE}>Learn more</a></EnglishText>
                <HebrewText> הוסיפו טקסטים מספריא ישירות לקובץ גוגל עם <span id="newExtension">התוסף החדש</span> שלנו! <a href={learnMoreLink} data-target-module={Sefaria.VOICES_MODULE}>למדו עוד</a></HebrewText>
             </InterfaceText>;
}
const GDocAdvertBox = React.memo(() => {
    const gdocsCampaignId = "GDocs_Promo_AddToSheet";
    const installNowLink = 'https://workspace.google.com/marketplace/app/sefaria/849562338091?utm_source=SefariaOrg&utm_medium=SidebarAdButton&utm_campaign=AddToSheetPromotion&utm_content=InstallFromAddToSheet';
    const gdocsCampaignAd = {campaignId: gdocsCampaignId};
    const gdocInstalled = 'gdoc_installed';
    const handleInstall = () => {
        cookie(gdocInstalled, JSON.stringify(1), {path: "/"});
        trackSidebarAdClick(gdocsCampaignAd);
    }
    return !cookie(gdocInstalled) &&
        <OnInView onVisible={() => trackSidebarAdImpression(gdocsCampaignAd)}>
            <div className="gDocAdvertBox">
              <GDocAdvertText/>
              <div id="installNow">
                <a 
                  href={installNowLink}
                  onClick={handleInstall}
                  onKeyDown={(e) => Util.handleLinkSpaceKey(e, handleInstall)}
                >
                  <InterfaceText>promotions.install_now</InterfaceText>
                </a>
              </div>
            </div>
          </OnInView>;
});

// Don't continuously rerender a SidebarAd if the parent component decides to rerender
// This is done to prevent multiple views from registering from OnInView
const SidebarAd = React.memo(({ context, matchingAd }) => {
  // The background color mirrors the banner: a hex string from Strapi applied inline, nothing
  // when null. The "colored" treatment (padding, white title/body, white button) applies only
  // when the color is DARK — a whitish background keeps the ad's default styling, so a light
  // tint reads exactly like an uncolored ad (design decision 2026-09-15).
  const backgroundColor = matchingAd.backgroundColor;
  const useColoredTreatment = !!backgroundColor && !isLightBackground(backgroundColor);
  const classes = classNames({
    sidebarPromo: 1,
    colored: useColoredTreatment,
  });

  function getButton() {
    return (
      <a
        className="button small"
        href={matchingAd.buttonURL}
        onClick={() => trackSidebarAdClick(matchingAd)}
        // Under the colored treatment the button is white with text in the ad's own color —
        // the generalization of the old blue ads' white-button-blue-text look.
        style={useColoredTreatment ? { color: backgroundColor } : undefined}
      >
        {matchingAd.buttonIcon ? (
          <img
            // TODO: Create middleware to handle serving media assets to distinguish between different environments
            // The absolute path is needed for debugging purposes to get the media asset from the local Strapi server
            // The local Strapi instance provides a relative path through the API
            src={(matchingAd.debug ? STRAPI_INSTANCE : '') + matchingAd.buttonIcon.url}
            alt={matchingAd.buttonIcon.alternativeText}
            aria-hidden="true"
          />
        ) : null}
        {matchingAd.buttonText}
      </a>
    );
  }

    const isHebrew = context.interfaceLang === "hebrew";
    const getLanguageClass = () => (isHebrew ? "int-he" : "int-en");

    // The body renders MARKDOWN, matching banners and modals (their body text flows through the
    // same InterfaceText markdown path with the same Strapi newline handling; the title stays
    // plain, like a banner/modal header). One difference from those surfaces: each sidebar-ad
    // object is single-language — one ad per locale, and matching already guarantees the ad's
    // language equals the interface language — so the {en}/{he} object InterfaceText expects is
    // built from whichever language this ad is.
    function getBodyText() {
      const bodyByLanguage = isHebrew ? { he: matchingAd.bodyText } : { en: matchingAd.bodyText };
      return (
        <p className={getLanguageClass() + " line-break"}>
          <InterfaceText
            markdown={replaceNewLinesWithLinebreaks(bodyByLanguage, { mode: "strapi" })}
          />
        </p>
      );
    }

    return (
      <OnInView onVisible={() => trackSidebarAdImpression(matchingAd)}>
        <div className={classes} style={backgroundColor ? { backgroundColor } : undefined}>
          <h3 className={getLanguageClass()}>{matchingAd.title}</h3>
          {matchingAd.buttonLocation === "below" ? (
            matchingAd.isNewsletterSubscriptionInputForm ? (
              <>
                {getBodyText()}
                <NewsletterSignUpForm
                  context={"Sidebar Ad: " + context.keywordTargets.toString()}
                  includeEducatorOption={false}
                  additionalNewsletterMailingLists={matchingAd.newsletterMailingLists}
                />
              </>
            ) : (
              <>
                {getBodyText()}
                {getButton()}
              </>
            )
          ) : matchingAd.isNewsletterSubscriptionInputForm ? (
            <>
              <NewsletterSignUpForm
                context={"Sidebar Ad: " + context.keywordTargets.toString()}
                includeEducatorOption={false}
                additionalNewsletterMailingLists={matchingAd.newsletterMailingLists}
              />
              {getBodyText()}
            </>
          ) : (
            <>
              {getButton()}
              {getBodyText()}
            </>
          )}
        </div>
      </OnInView>
    );
});

export { Promotions, GDocAdvertBox };
